import { read, TABLE } from '../data/db';
import { flagResolutions, matchNotes, matches, observations, teams } from '../data/repos';
import { previewAdjustment } from '../domain/commands';
import type { AdjustmentPreview, RederivableRow } from '../domain/commands';
import type { MatchAnchors, MatchRow } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { StorageError } from './errors';

/**
 * DIST-1 / SES-8 — the **I/O half** of the Triage screen (build unit U-14a).
 *
 * §1.2 forbids `ui/` from importing `data/`, so everything the screen reads arrives through here and
 * every write leaves through `dispatch`. The *decisions* are elsewhere: `domain/commands/flags.ts`
 * owns which flags may be resolved, `domain/commands/boundaries.ts` owns SES-8's window and computes
 * every re-derived minute, and `domain/machine.ts` owns `EnterTriage`/`PassTriage`/`Defer`.
 *
 * ── The queue is NOT re-implemented here ────────────────────────────────────
 *
 * `app/flags.ts` already loads DIST-1's derived queue with §10.1's ordering, §8.2's audio captions
 * and the three promotion paths, and `ui/halftime/useFlags.ts` already controls them. The Triage
 * screen uses both unchanged. A second loader would be a second answer to *"which flags are
 * unresolved"*, which is the one thing DIST-1's append-only design exists to prevent — and the
 * halftime sheet and the Triage screen showing different queues for one match is precisely the bug
 * that would not be found until a venue.
 *
 * What is genuinely new here is the *screen's own* context: the match (for §10.1's SES-8 window and
 * the anchors themselves), the opponent's name, and the rows SES-8 would re-derive.
 */

/** Everything the Triage screen needs that is not the flag queue. */
export interface TriageContext {
  readonly match: MatchRow;
  readonly opponent: string;
  /** DIST-1 — `PassTriage`'s guard, read so the screen can say *why* the control is unavailable. */
  readonly unresolvedCount: number;
  /** SES-8's inputs. Empty is a legitimate answer, and the preview reports it as one. */
  readonly observations: readonly RederivableRow[];
  readonly notes: readonly RederivableRow[];
}

/**
 * One readonly transaction for the whole screen.
 *
 * One, for the reason `loadFlagsContext` gives: the match state, the unresolved count and the rows a
 * boundary adjustment would move describe **one moment**. A screen that read the state before a
 * resolve and the count after it would offer *Pass triage* against a queue it is no longer showing.
 *
 * @throws {StorageError} on a read failure, and on a match that does not exist. Not swallowed: a
 *   missing match and an empty queue look identical on screen, and only one of them is normal.
 */
export async function loadTriageContext(matchId: string): Promise<TriageContext> {
  return read(
    [TABLE.matches, TABLE.teams, TABLE.observations, TABLE.flagResolutions, TABLE.matchNotes],
    async () => {
      const match = await matches.getMatch(matchId);
      if (match === undefined) {
        throw new StorageError('That match no longer exists.', {
          context: { store: TABLE.matches, matchId },
        });
      }
      const [unresolved, rows, notes] = await Promise.all([
        flagResolutions.listUnresolvedFlags(matchId),
        observations.listObservationsForMatch(matchId),
        matchNotes.listForMatch(matchId),
      ]);
      const opponent = await teams.getTeam(match.teamId);
      return {
        match,
        opponent: opponent?.name ?? 'Opponent',
        unresolvedCount: unresolved.length,
        observations: rows.map(toRederivable),
        notes: notes.map(toRederivable),
      };
    },
    'loadTriageContext',
  );
}

function toRederivable(row: { id: string; createdAt: number; matchMinute: number }): RederivableRow {
  return { id: row.id, createdAt: row.createdAt, matchMinute: row.matchMinute };
}

/**
 * §2.3a — the count on the `⋯` overflow sheet's *Review flags (n)* row.
 *
 * ── Why a count read and not `loadTriageContext` ────────────────────────────
 *
 * The row needs one number. `loadTriageContext` opens five stores and materialises every Observation
 * and MatchNote of the match so SES-8 can preview a re-derivation — on a surface where the user has
 * tapped `⋯` mid-half, possibly to reach Abandon. Reusing it would put the cost of the *screen* on
 * the *menu*, and NFR-1's budget is not spent on a row nobody may tap.
 *
 * It is nonetheless the **same derivation**: `listUnresolvedFlags` is the one answer to *"which flags
 * are unresolved"* (DIST-1's append-only design), so this row and the screen it opens cannot
 * disagree about what is waiting.
 *
 * ── The count is a claim about a moment, and the row is honest about that ───
 *
 * §2.3a permits the row to be *"inert at zero"*, which is a rendering decision made from this number.
 * Nothing depends on it being fresh: opening the screen re-reads, and `handleResolveFlag` re-reads
 * inside its own transaction. What the count may **never** do is condition the `⋯` button itself —
 * that negative lives in `CaptureChrome`, where the button is, not here.
 *
 * @throws {StorageError} on a read failure. The caller renders the row inert rather than the sheet
 *   failing: a menu that cannot be opened because a count could not be read would take SES-6's
 *   Abandon down with it.
 */
export async function countUnresolvedFlags(matchId: string): Promise<number> {
  const unresolved = await read(
    [TABLE.observations, TABLE.flagResolutions],
    () => flagResolutions.listUnresolvedFlags(matchId),
    'countUnresolvedFlags',
  );
  return unresolved.length;
}

/**
 * §10.1's *"live preview of the resulting minute range before commit"*.
 *
 * Deliberately a **re-export of the handler's own function**, not a second calculation. If the
 * preview and the commit could disagree, the preview would be worse than absent: it is the only
 * thing standing between the user and the product's most destructive control.
 */
export function previewBoundaries(
  context: TriageContext,
  anchors: Omit<MatchAnchors, 'halfLengthMin'>,
): AdjustmentPreview {
  return previewAdjustment(anchors, context.match.halfLengthMin, context.observations, context.notes);
}

// ── The three lifecycle moves the screen can make (§2.2) ─────────────────────

/** SES-11 — *Distill now* on the Full Time offer. `fullTime → triage`, no guard. */
export function enterTriage(matchId: string): Promise<DispatchResult> {
  return dispatch({ type: 'Transition', event: 'EnterTriage', matchId });
}

/**
 * DIST-1 — *"must be passed, possibly empty, before distillation"*. `triage → distill`.
 *
 * Guarded in `machine.ts` on **zero unresolved flags** and rejected with `ERR_UNRESOLVED_FLAGS`. The
 * screen also disables the control, and the two are not redundant: the disable is what the user
 * reads, the guard is what makes it true when a resolve fails or a second surface raced this one.
 */
export function passTriage(matchId: string): Promise<DispatchResult> {
  return dispatch({ type: 'Transition', event: 'PassTriage', matchId });
}

/** §2.2 — *Defer*: `triage`/`distill → fullTime`. SES-11's "later", taken from inside triage. */
export function deferTriage(matchId: string): Promise<DispatchResult> {
  return dispatch({ type: 'Transition', event: 'Defer', matchId });
}

/** SES-8 — commit an adjustment. Every guard, and every minute, belongs to the handler. */
export function adjustBoundaries(
  matchId: string,
  anchors: Omit<MatchAnchors, 'halfLengthMin'>,
): Promise<DispatchResult> {
  return dispatch({ type: 'AdjustBoundaries', matchId, anchors });
}
